// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { useCallback, useRef, useId } from 'react';
import { DECIMAL_SEPARATOR, MathUtils } from '@shared/utils/MathUtils';
import { numericFormatter } from 'react-number-format';
export function useBaseNumeric({ value, step, onValueChange, min, max, precision, allowLeadingZeros, allowNegative, disabled, isOnlyMouseWheel, onIncrement, onDecrement, format, onBlur, onKeyDown }) {
    //#region State
    const inputId = useId();
    const valueRef = useRef(value);
    valueRef.current = value;
    const onIncrementRef = useRef(onIncrement);
    onIncrementRef.current = onIncrement;
    const onDecrementRef = useRef(onDecrement);
    onDecrementRef.current = onDecrement;
    const baseFormatOptions = {
        allowLeadingZeros,
        allowNegative,
        decimalSeparator: DECIMAL_SEPARATOR,
        allowedDecimalSeparators: ['.', ','],
        decimalScale: precision !== null && precision !== void 0 ? precision : MathUtils.GetDecimalPlaces(value),
        min,
        max,
        step
    };
    //#endregion
    //#region Helpers
    const formatNumber = useCallback((val) => {
        if (isNullOrUndefined(format)) {
            return numericFormatter(val, Object.assign(Object.assign({}, baseFormatOptions), { displayType: 'text' }));
        }
        else {
            return format(val);
        }
    }, [baseFormatOptions, format]);
    const notifyValueChangeRef = useRef(onValueChange);
    notifyValueChangeRef.current = onValueChange;
    const stepIncrement = useCallback(() => {
        const currentValue = valueRef.current;
        const incrementCallback = onIncrementRef.current;
        if (isNullOrUndefined(incrementCallback)) {
            return;
        }
        const newValue = incrementCallback(currentValue);
        notifyValueChangeRef.current(newValue);
    }, []);
    const stepDecrement = useCallback(() => {
        const currentValue = valueRef.current;
        const decrementCallback = onDecrementRef.current;
        if (isNullOrUndefined(decrementCallback)) {
            return;
        }
        const newValue = decrementCallback(currentValue);
        notifyValueChangeRef.current(newValue);
    }, []);
    //#endregion
    //#region Handlers
    const handleBlur = useCallback((e) => {
        let correctedValue = value;
        if (isValidNumber(max) && correctedValue > max) {
            correctedValue = max;
        }
        else if (isValidNumber(min) && correctedValue < min) {
            correctedValue = min;
        }
        if (correctedValue !== value) {
            onValueChange(correctedValue);
        }
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    }, [min, max, onValueChange, onBlur, value]);
    const handleWheel = useCallback((e) => {
        e.stopPropagation();
        if (e.deltaY < 0) {
            stepIncrement();
        }
        else {
            stepDecrement();
        }
    }, [stepIncrement, stepDecrement]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            stepIncrement();
        }
        else if (e.key === 'ArrowDown') {
            e.preventDefault();
            stepDecrement();
        }
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
    }, [stepIncrement, stepDecrement, onKeyDown]);
    //#endregion
    //#region Input props
    const numericInputProps = Object.assign(Object.assign({ id: inputId, displayType: 'input' }, baseFormatOptions), { value,
        disabled, onValueChange: (values) => onValueChange(values.floatValue), onBlur: handleBlur, onWheel: handleWheel, onKeyDown: handleKeyDown, autoComplete: 'off', autoCorrect: 'off', readOnly: isOnlyMouseWheel });
    //#endregion
    return {
        numericInputProps,
        formatNumber
    };
}
